<script lang="ts">
  /**
   * The New game picker: the shell's `PickerPage` (a full page on the sunken
   * ground with Start always in view), opened by Home's one filled tile. It
   * is the *solo* picker; playing with or against somebody starts on the
   * Together tab (`docs/decisions/0054`).
   *
   * The questions, reading down: which mode (two tiles), the goal and its
   * target (Sprint only: two short-word choices, so two rows with a pill
   * segment at the right), and how hard (four cards, each saying what it
   * means): `ModeQuestions.svelte`, which the setup of a round on one device
   * asks too. The summary under them is your personal best and the global
   * record for exactly this configuration, and Start names what it will
   * start.
   *
   * Your own best is on this device; the record is everybody's. The two are
   * never the same word (`docs/decisions/0045`).
   *
   * The Daily is not a value of this picker: it is its own card with its own
   * action, so this only ever describes Sprint or Survival.
   */
  import { Icon, PickerPage, Row } from '@artibens/app-shell';
  import { encodeModeConfig } from '@interlock/modes';
  import type { ModeConfig } from '@interlock/modes';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import { loadPersonalBest } from '../state/personal-bests-io.js';
  import { records, recordFor } from '../state/records.svelte.js';
  import ModeQuestions from './ModeQuestions.svelte';
  import { configFromChoices, defaultModeChoices, describeChoice } from './mode-choices.js';
  import type { ModeChoices } from './mode-choices.js';

  const {
    open,
    onclose,
    onStart,
  }: {
    open: boolean;
    onclose: () => void;
    onStart: (config: ModeConfig) => void;
  } = $props();

  let choices: ModeChoices = $state(defaultModeChoices());

  const activeConfig = $derived(configFromChoices(choices));
  const modeKey = $derived(encodeModeConfig(activeConfig));
  // Reads `open`: this device's best is read again on every visit, because
  // `localStorage` is not reactive and a run may have just set one.
  const best = $derived(open ? loadPersonalBest(modeKey) : null);
  const record = $derived(recordFor(modeKey));

  /** "Sprint · Hard · 40 lines": the mode, then its configuration as the HUD will show it. */
  const choiceLabel = $derived(describeChoice(activeConfig));

  function start(): void {
    // The AudioContext may only start on a user gesture; this tap is it.
    startAudioForGesture();
    onclose();
    onStart(activeConfig);
  }
</script>

{#snippet sections()}
  <ModeQuestions bind:choices />
{/snippet}

{#snippet summary()}
  <Row
    label="Your personal best"
    value={best ? best.formatted : '—'}
    testid="best-line"
    valueTestid="best-line-value"
  />
  {#if records.status === 'ready'}
    <Row
      label="Global record"
      value={record ? `${record.formatted} · ${record.username}` : '—'}
      testid="record-line"
    />
  {/if}
{/snippet}

{#snippet playIcon()}<Icon name="play" size={18} />{/snippet}

<PickerPage
  {open}
  title="New game"
  {onclose}
  {sections}
  {summary}
  summaryLabel="This configuration"
  start={{ label: `Start · ${choiceLabel}`, icon: playIcon, onclick: start, testid: 'start-run' }}
  testid="solo-picker"
  testids={{ summary: 'solo-records' }}
/>
